import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowForwardCallStep } from 'entities/SmartCall/api/types'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { NumberSettingStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/ForwardCall/ui/ForwardCallActionEditContent/NumberSetting/store/NumberSettingStore'
import {
  allSmartCallFlowActionTypes,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type IForwardCallStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowForwardCallStep['args']
}

export class ForwardCallStore {
  static readonly type = 'forwardCall' satisfies ISmartCallFlowActionType
  static title = 'Forward call'
  static iconProps: IIconProps = {
    icon: 'phoneTransfer',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore

  constructor(
    readonly entities: IEntitiesStore,
    { id, args, ...props }: IForwardCallStoreProps,
    private parent: {
      inbox: Inbox | null
    }
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: ForwardCallStore.type,
      name: ForwardCallStore.title,
      allowedBeforeActions: allSmartCallFlowActionTypes.filter((type) => type !== 'forwardCall'),
      allowedNextActionsByBranchIndex: [
        ['aiTextingAgent'],
        allSmartCallFlowActionTypes.filter((type) => type !== 'forwardCall'),
      ],
      edgeType: 'customEdgeWithLabel',
    })

    this._numbersSettingsMap = new Map(
      this.parent.inbox?.numbers.map((number) => {
        const config = args?.numbers?.find((item) => item.number_id === number.id)
        return [number.id, new NumberSettingStore(this.entities, number, config)]
      })
    )

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowForwardCallStep, 'id' | 'type'> = {
    id: 'forwardCallMulti',
    type: 'action',
  }

  get step(): ISmartCallFlowForwardCallStep {
    const nextIds = this.actionStore.nextIds
    return {
      ...this._stepIdType,
      args: {
        next_on_transferred: nextIds[0] ?? '',
        next_on_no_transferred: nextIds[1] ?? '',
        numbers: this.numbersSettings.map((number) => number.params),
      },
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {}

  private _isShowConfigurationError = false
  validate = () => {
    if (this.isAutoRechargeRequired) return false
    if (this._numbersSettingsMap.size === 1) {
      if (!this.numbersSettings[0].validate()) {
        return false
      }
    } else {
      if (!this.numbersSettings.some((item) => item.isConfigured)) {
        this._isShowConfigurationError = true
        return false
      }
    }

    return true
  }

  get isAutoRechargeRequired() {
    return !this.entities.billingStore.autorecharge?.is_active
  }

  get isConfigurationError() {
    if (this.isAutoRechargeRequired) {
      return 'Auto-Recharge required'
    }
    if (this._numbersSettingsMap.size === 1) {
      return this.numbersSettings[0].isConfigurationError
    } else {
      if (
        this._isShowConfigurationError &&
        !this.numbersSettings.some((item) => item.isConfigured)
      ) {
        return true
      }
    }

    return false
  }

  private _numbersSettingsMap: Map<number, NumberSettingStore>
  replaceNumberSettings = (id: number, store: NumberSettingStore) => {
    this._numbersSettingsMap.set(id, store)
    this._isShowConfigurationError = false
  }

  get numbersSettings() {
    return Array.from(this._numbersSettingsMap.values())
  }

  get isSingleNumberInbox() {
    return this.numbersSettings.length === 1
  }

  get configuredNumbers() {
    return this.numbersSettings.filter((item) => item.isConfigured)
  }

  get notConfiguredNumbers() {
    return this.numbersSettings.filter((item) => !item.isConfigured)
  }

  private _singleBranchLabels = ['Call transferred', 'Call not transferred']
  private _smartBranchLabels = ['Forwarding set', 'No forwarding set']

  getBranchNameByTargetId = (id: string) => {
    const dataIndex = this.actionStore.nextIds.indexOf(id)
    if (dataIndex !== -1) {
      return (this.isSingleNumberInbox ? this._singleBranchLabels : this._smartBranchLabels)[
        dataIndex
      ]
    }
    return 'Branch label'
  }
}
